import { LocalDate } from "@js-joda/core";
import { z } from "zod";

/**
 * Type guard for LocalDate instances
 */
const isLocalDate = (val: unknown): val is LocalDate => val instanceof LocalDate;

/**
 * Interface for serialized LocalDate objects from API
 * The API may serialize LocalDate instances as plain objects with year/month/day properties
 */
interface LocalDateLike {
    _year: number;
    _month: number;
    _day: number;
}

/**
 * Type guard for LocalDate-like objects from API serialization
 */
const isLocalDateLike = (val: unknown): val is LocalDateLike => {
    return (
        typeof val === "object" &&
        val !== null &&
        "_year" in val &&
        "_month" in val &&
        "_day" in val
    );
};

/**
 * Converts various input formats to LocalDate instance
 *
 * Handles:
 * - LocalDate instances (passthrough)
 * - ISO date strings (e.g., "2024-01-15")
 * - Serialized LocalDate objects from API ({_year, _month, _day})
 * - null/undefined
 *
 * @param val - Input value to convert
 * @returns LocalDate instance or null if conversion fails
 */
function toLocalDate(val: unknown): LocalDate | null {
    if (val === null || val === undefined) {
        return null;
    }

    // Already a LocalDate instance
    if (isLocalDate(val)) {
        return val;
    }

    // ISO date string
    if (typeof val === "string") {
        try {
            return LocalDate.parse(val);
        } catch (error) {
            if (import.meta.env.DEV) {
                console.warn("Failed to parse date string:", val, error);
            }
            return null;
        }
    }

    // Serialized LocalDate object from API
    if (isLocalDateLike(val)) {
        try {
            return LocalDate.of(val._year, val._month, val._day);
        } catch (error) {
            if (import.meta.env.DEV) {
                console.warn("Failed to construct LocalDate from object:", val, error);
            }
            return null;
        }
    }

    if (import.meta.env.DEV) {
        console.warn("Unexpected date value type:", typeof val, val);
    }
    return null;
}

/**
 * Zod transformer for LocalDate fields
 *
 * Accepts LocalDate instances, ISO date strings, or serialized LocalDate objects.
 * Returns LocalDate | null.
 *
 * Use this for fields that need LocalDate instances for date manipulation.
 */
export const localDateTransformer = z
    .union([
        z.custom<LocalDate>(isLocalDate),
        z.string(),
        z
            .object({
                _year: z.number(),
                _month: z.number(),
                _day: z.number(),
            })
            .passthrough(),
        z.null(),
        z.undefined(),
    ])
    .transform(toLocalDate)
    .nullable();

/**
 * Zod transformer for date string fields
 *
 * Accepts LocalDate instances, ISO date strings, or serialized LocalDate objects.
 * Returns ISO date string | null | undefined.
 *
 * Use this for optional date fields that should be stored as strings.
 * The .optional() modifier makes the entire field optional in the schema,
 * meaning the field can be omitted entirely (undefined).
 */
export const dateStringTransformer = z
    .union([
        z.string(),
        z.custom<LocalDate>(isLocalDate),
        z
            .object({
                _year: z.number(),
                _month: z.number(),
                _day: z.number(),
            })
            .passthrough(),
        z.null(),
        z.undefined(),
    ])
    .transform((val) => {
        // Already a string - validate it's a valid date format
        if (typeof val === "string") {
            try {
                // Validate by parsing
                LocalDate.parse(val);
                return val;
            } catch (error) {
                if (import.meta.env.DEV) {
                    console.warn("Invalid date string:", val, error);
                }
                return null;
            }
        }

        // Convert to LocalDate first, then to string
        const date = toLocalDate(val);
        return date ? date.toString() : null;
    })
    .nullable()
    .optional();
